import { useEffect, useState } from "react";
import { ExternalLink, X } from "lucide-react";
import { useGitCommit } from "@/hooks/git";
import type { GitPrFile } from "@/services/git/types";
import { CommitPaneSkeleton, PaneEmpty } from "./shared";
import { DiffPane } from "./DiffPane";

// ─── Commit Detail Pane ───────────────────────────────────────────────────────

export function CommitDetailPane({ repoFullName, sha, onClose }: { repoFullName: string; sha: string; onClose: () => void }) {
  const [selectedFile, setSelectedFile] = useState<GitPrFile | null>(null);
  const { data: commit, isLoading } = useGitCommit(repoFullName, sha);

  useEffect(() => { setSelectedFile(null); }, [sha]);
  useEffect(() => { if (commit?.files?.[0]) setSelectedFile(commit.files[0]); }, [commit]);

  if (isLoading) return <CommitPaneSkeleton />;
  if (!commit) return <PaneEmpty text="Commit not found." />;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Meta header */}
      <div className="flex items-start justify-between gap-3 border-b border-border bg-muted/20 px-5 py-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{commit.message.split("\n")[0]}</p>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-xs text-muted-foreground">
            <span className="text-primary/80">{commit.sha.slice(0, 7)}</span>
            <span>{commit.author_name}</span>
            {commit.committed_at && <span>{new Date(commit.committed_at).toLocaleDateString()}</span>}
            <span className="text-green-500">+{commit.additions}</span>
            <span className="text-red-400">−{commit.deletions}</span>
            <span>{commit.files.length} file{commit.files.length !== 1 ? "s" : ""}</span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <a href={commit.html_url} target="_blank" rel="noreferrer" className="studio-button text-xs">
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <button type="button" onClick={onClose} className="studio-button text-xs">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* File list + Monaco */}
      <DiffPane files={commit.files} selectedFile={selectedFile} onSelectFile={setSelectedFile} />
    </div>
  );
}
